/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { processExcerpt } from '../../../../../../../common/utils/search-result-utils';
import { convertVideoDuration } from './convert-video-duration';
import { createGongChapterUrl } from './create-gong-chapter-url';
import { messages } from './messages';
import {
	BaseVideoSearchResult,
	type BaseVideoSearchResultProps,
} from './shared/base-video-search-result';
import type { VideoResultRendererProps } from './shared/types';
import { VideoChapters } from './shared/video-chapters';

const CRM_ACCOUNT_NAME_FIELD_ID = 'crmAccountName';

type RelayVideo = VideoResultRendererProps['data'];
type RelayVideoEntity = NonNullable<RelayVideo['entity']>;
type RelayChapter = NonNullable<NonNullable<RelayVideoEntity['chapters']>[number]>;
type RelayChapterWithStartTime = RelayChapter & {
	readonly startTimeInSeconds: NonNullable<RelayChapter['startTimeInSeconds']>;
};

const hasStartTime = (
	chapter: RelayChapter | null | undefined,
): chapter is RelayChapterWithStartTime =>
	chapter?.startTimeInSeconds !== null && chapter?.startTimeInSeconds !== undefined;

const groupGongTopicsByStartTime = (
	chapters: RelayChapterWithStartTime[],
	formatList: ReturnType<typeof useIntl>['formatList'],
): Array<{ readonly title: string; readonly startTimeInSeconds: number }> => {
	const topicsByStartTime = new Map<number, RelayChapterWithStartTime[]>();

	chapters.forEach((chapter) => {
		const topicsAtStartTime = topicsByStartTime.get(chapter.startTimeInSeconds) ?? [];
		topicsAtStartTime.push(chapter);
		topicsByStartTime.set(chapter.startTimeInSeconds, topicsAtStartTime);
	});

	return Array.from(topicsByStartTime.values(), (topicsAtStartTime) => ({
		title: formatList(
			topicsAtStartTime
				.map((topic) => topic.title?.trim())
				.filter((title): title is string => Boolean(title)),
			{ style: 'long', type: 'conjunction' },
		),
		startTimeInSeconds: topicsAtStartTime[0].startTimeInSeconds,
	}));
};

/**
 * Renders Gong videos with their CRM account context and text-first result body.
 *
 * `entityExtendedValues` is supplied by the Search GraphQL schema. The renderer
 * deliberately reads only the displayable CRM account field instead of rendering
 * arbitrary connector-provided values.
 */
export const GongVideoRenderer: (props: VideoResultRendererProps) => JSX.Element = (
	props: VideoResultRendererProps,
) => {
	const { data, formatMessage } = props;
	const { formatList } = useIntl();
	const { entity, excerpt, url } = data;
	const { chapters, durationInSeconds, embedUrl, entityExtendedValues } = entity || {};

	const accountName = entityExtendedValues
		?.find(
			(extendedEntityValue) =>
				extendedEntityValue?.fieldId === CRM_ACCOUNT_NAME_FIELD_ID &&
				extendedEntityValue?.isDisplayed === true,
		)
		?.fieldValue?.trim();

	const videoDuration = convertVideoDuration(durationInSeconds, { readable: true, formatMessage });
	const validChapters = (chapters ?? [])
		.filter(hasStartTime)
		.sort(
			(firstChapter, secondChapter) =>
				firstChapter.startTimeInSeconds - secondChapter.startTimeInSeconds,
		);
	const groupedTopics = groupGongTopicsByStartTime(validChapters, formatList);
	const metadataParts: NonNullable<BaseVideoSearchResultProps['additionalMetadataParts']> = [
		['video-duration', videoDuration],
		['crm-account', accountName],
	];

	return (
		<BaseVideoSearchResult
			{...props}
			body={<DescriptionBody description={processExcerpt(excerpt, true)} />}
			footer={
				<VideoChapters
					chapters={groupedTopics}
					url={embedUrl || url}
					getChapterUrl={(startTimeInSeconds, chapterIndex) =>
						embedUrl && durationInSeconds !== null && durationInSeconds !== undefined
							? createGongChapterUrl(
									embedUrl,
									startTimeInSeconds,
									groupedTopics[chapterIndex + 1]?.startTimeInSeconds ?? durationInSeconds - 1,
								)
							: embedUrl || url
					}
					dropdownMessage={formatMessage(messages.gongTopics, {
						chapterCount: groupedTopics.length,
					})}
				/>
			}
			additionalMetadataParts={metadataParts}
		/>
	);
};
